import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import {
  roundTrips,
  lintDescription,
  appendGallery,
  galleryHtml,
  galleryAlt,
  hasGalleryHeading,
  countImages,
  describeReadBackLoss,
} from "./newsEditorUtils";
import { insertAfterCursorBlock } from "./newsEditorInsert";
import { createNewsUploadAdapterPlugin, uploadNewsImage, validateNewsImage } from "./newsUpload";
import { categoryPageHint } from "./newsSections";
import "../../../components/common/css/new.scss";
import "./addnew.scss";

// Defense-in-depth allowlist for the optional event URL. `type="url"` still
// accepts dangerous schemes (e.g. `javascript:`), so restrict the protocol to
// http/https via the URL() constructor — OWASP "Allow-list http and HTTPS URLs
// only". Returns "" for empty (valid, optional), the canonical href for a valid
// http(s) URL, or null for anything invalid. MUST be mirrored server-side and
// wherever the portal renders this value into an href.
function normalizeEventUrl(value) {
  const raw = (value || "").trim();
  if (!raw) return "";
  let parsed;
  try {
    parsed = new URL(raw);
  } catch (_) {
    return null; // unparseable or relative
  }
  return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
}

// The stock toolbar minus Media Embed: its <oembed> output is removed by the
// server, so a video would silently disappear. A pasted YouTube link becomes a
// player on the site instead.
const EDITOR_TOOLBAR = [
  "heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "|",
  "outdent", "indent", "|", "uploadImage", "blockQuote", "insertTable", "|", "undo", "redo",
];

// Banners render in a 16:9 box on the site; anything else is letterboxed.
const BANNER_RATIO = 16 / 9;

const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const NewsAddEdit = () => {
  const { newsId } = useParams();
  const isAdd = !newsId;

  const statusOptions = [
    { value: 0, label: "Draft" },
    { value: 1, label: "Published" },
  ];

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState([]);
  const [status, setStatus] = useState(statusOptions[0]);
  const [title, setTitle] = useState("");
  // The description as STORED (immutable after load). An untouched save sends
  // exactly this, so opening a post never reshapes it.
  const [loadedHtml, setLoadedHtml] = useState(isAdd ? "" : null);
  // The working copy: written by the visual editor (after the guard) or the HTML box.
  const [draft, setDraft] = useState("");
  const [touched, setTouched] = useState(false);
  // "visual" (CKEditor) or "html" (raw textarea).
  const [mode, setMode] = useState("visual");
  // Round-trip guard: until CKEditor has loaded the stored HTML and we have
  // compared its output, editor writes are ignored.
  const [guard, setGuard] = useState({ checked: isAdd, mismatch: false });
  const [editorKey, setEditorKey] = useState(0);
  const [conversion, setConversion] = useState(null); // { snapshot, changed }
  const [gallery, setGallery] = useState({ busy: false, done: 0, total: 0 });
  const [eventUrl, setEventUrl] = useState("");
  // Optional date of the event itself, as YYYY-MM-DD for <input type="date">.
  const [eventDate, setEventDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [existingBannerURL, setExistingBannerURL] = useState(null);
  const [bannerWarning, setBannerWarning] = useState("");
  const [errors, setErrors] = useState({});
  const editorRef = useRef(null);
  // CKEditor PendingActions: true while a toolbar/paste image upload is in flight.
  const [pendingUploads, setPendingUploads] = useState(false);
  // The latest render's state, for async completions (a gallery upload) that
  // must not act on the values their closure captured when they started.
  const latest = useRef({});
  // True once the author has been in the Visual editor: its cursor then marks
  // where "Add gallery images" inserts. Never focused = append at the end.
  const cursorPlaced = useRef(false);
  const galleryInputRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");
  const apiBase = process.env.REACT_APP_API;

  const uploadPlugin = useMemo(
    () => createNewsUploadAdapterPlugin({ apiBase, getToken: () => localStorage.getItem("accessToken") }),
    [apiBase]
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${apiBase}admin/category-news`, {
          headers: { "x-access-token": token },
        });
        setCategories(response.data.data.rows.map((d) => ({ value: d.id, label: d.category })));
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to load categories";
        Swal.fire({ title: errorMessage, icon: "error", confirmButtonText: "OK" });
      }
    };

    const fetchDataById = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiBase}admin/news/${newsId}`, {
          headers: { "x-access-token": token },
        });
        const data = response.data.data;
        setTitle(data.title);
        // ORDER MATTERS: React 17 does not batch updates in async code, and the
        // editor mounts as soon as loadedHtml is set. Setting the draft first
        // means CKEditor mounts with the stored HTML, so the round-trip guard
        // compares the real post and not an empty one.
        setDraft(data.description || "");
        setLoadedHtml(data.description || "");
        // Normalize on load so a legacy/non-conforming stored value can't block
        // saving the rest of the form — a bad value is dropped to "".
        setEventUrl(normalizeEventUrl(data.event_url) || "");
        // Slice the ISO string rather than going through Date(), which would
        // shift the day for editors west of Greenwich.
        setEventDate(data.event_date ? String(data.event_date).slice(0, 10) : "");
        setStatus(statusOptions.find((opt) => opt.value === data.status));
        setCategoryId(data.category.map((cat) => cat.id));
        setExistingBannerURL(data.bannerURL);
        setFilePreview(data.bannerURL);
        setSelectedFile(null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        const errorMessage = error.response?.data?.message || error.message || "Failed to load news data";
        Swal.fire({ title: errorMessage, icon: "error", confirmButtonText: "OK" });
      }
    };

    fetchCategories();
    if (!isAdd) fetchDataById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdd, newsId, token]);

  const categoryNames = categories.filter((c) => categoryId.includes(c.value)).map((c) => c.label);
  latest.current = { draft, touched, mode, loadedHtml, guardChecked: guard.checked, pendingUploads };

  // The description that would be saved right now. Reads the LATEST render's
  // state, so an async completion (a gallery upload) never acts on stale values.
  const currentDescription = () => {
    const now = latest.current;
    if (!isAdd && !now.touched) return now.loadedHtml || "";
    if (now.mode === "visual" && editorRef.current && now.guardChecked) return editorRef.current.getData();
    return now.draft;
  };

  // While images upload into the gallery the author cannot edit (the editor is
  // read-only and the HTML box too), so the appended images never replace text.
  const lockEditor = (locked) => {
    const editor = editorRef.current;
    if (!editor) return;
    if (locked && typeof editor.enableReadOnlyMode === "function") editor.enableReadOnlyMode("news-gallery-upload");
    if (!locked && typeof editor.disableReadOnlyMode === "function") editor.disableReadOnlyMode("news-gallery-upload");
  };

  const handleEditorReady = (editor) => {
    editorRef.current = editor;
    cursorPlaced.current = false;
    const viewDocument = editor && editor.editing && editor.editing.view && editor.editing.view.document;
    if (viewDocument && typeof viewDocument.on === "function") {
      viewDocument.on("change:isFocused", (_evt, _name, isFocused) => {
        if (isFocused) cursorPlaced.current = true;
      });
    }
    const plugins = editor && editor.plugins;
    if (plugins && typeof plugins.has === "function" && plugins.has("PendingActions")) {
      const pending = plugins.get("PendingActions");
      setPendingUploads(!!pending.hasAny);
      pending.on("change:hasAny", (_evt, _name, value) => setPendingUploads(!!value));
    } else {
      setPendingUploads(false);
    }
    if (conversion && conversion.changed === null) {
      // Converting HTML -> Visual: tell the author if Visual changed anything.
      const out = editor.getData();
      const changed = !roundTrips(conversion.snapshot, out);
      setConversion({ snapshot: conversion.snapshot, changed });
      setDraft(out);
      return;
    }
    if (!guard.checked && loadedHtml !== null) {
      const out = editor.getData();
      if (roundTrips(loadedHtml, out)) {
        setGuard({ checked: true, mismatch: false });
      } else {
        // Visual mode would reshape this post: keep the stored HTML untouched
        // and switch to the HTML box before the author can type.
        setGuard({ checked: true, mismatch: true });
        setDraft(loadedHtml);
        editorRef.current = null;
        setMode("html");
      }
    }
  };

  const handleEditorChange = (_event, editor) => {
    if (!guard.checked || mode !== "visual") return;
    const data = editor.getData();
    setDraft(data);
    setTouched(true);
    setErrors((prev) => ({ ...prev, description: data.trim() ? "" : "Description is required" }));
  };

  const switchToHtml = () => {
    if (mode === "html" || pendingUploads || gallery.busy) return;
    if (editorRef.current && guard.checked && touched) setDraft(editorRef.current.getData());
    editorRef.current = null;
    setConversion(null);
    setMode("html");
  };

  const switchToVisual = async () => {
    if (mode === "visual" || gallery.busy) return;
    const result = await Swal.fire({
      title: "Switch to the visual editor?",
      text: guard.mismatch
        ? "This post contains HTML the visual editor does not support. Switching may change or remove parts of it (for example image attributes or layout). You can restore the HTML afterwards if something changes."
        : "The visual editor may reformat the HTML you typed. You can restore it afterwards if something changes.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Switch",
      cancelButtonText: "Stay in HTML",
    });
    if (!result || !result.isConfirmed) return;
    // Key BEFORE mode: React 17 does not batch after an await, and setting the
    // mode first mounted an editor that the key change then destroyed mid-init.
    setConversion({ snapshot: draft, changed: null });
    setTouched(true);
    setEditorKey((k) => k + 1);
    setMode("visual");
  };

  const restoreHtml = () => {
    if (!conversion) return;
    setDraft(conversion.snapshot);
    editorRef.current = null;
    setConversion(null);
    setMode("html");
  };

  const handleGalleryFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // A toolbar image still uploading inside CKEditor (Codex admin review): the gallery finishes by replacing
    // the editor's whole model (setData), which would drop that pending upload. One upload kind at a time.
    if (latest.current.pendingUploads) {
      Swal.fire({ title: "An image is still uploading", text: "Wait for it to finish, then add the gallery.", icon: "info" });
      return;
    }
    const problems = files.map((f) => validateNewsImage(f)).filter(Boolean);
    if (problems.length) {
      Swal.fire({ title: "Some files cannot be uploaded", text: problems[0], icon: "error" });
      return;
    }
    setGallery({ busy: true, done: 0, total: files.length });
    lockEditor(true);
    // Alt numbers go on from the images already in the post.
    const existingImages = countImages(currentDescription());
    const items = [];
    try {
      for (const [index, file] of files.entries()) {
        // One at a time: a 30-image gallery stays well inside the API rate limit
        // and a failure stops at the image that failed.
        const url = await uploadNewsImage(file, { apiBase, token });
        items.push({ url, alt: galleryAlt(title, existingImages, index) });
        setGallery({ busy: true, done: index + 1, total: files.length });
      }
    } catch (error) {
      lockEditor(false);
      setGallery({ busy: false, done: 0, total: 0 });
      Swal.fire({ title: "Upload failed", text: `${error.message} (${items.length} of ${files.length} uploaded; they were not added).`, icon: "error" });
      return;
    }
    lockEditor(false);
    const current = currentDescription();
    const editor = editorRef.current;
    // Visual mode, cursor placed: the images go right after the block the
    // cursor is in, which is where the public page will show them. Otherwise
    // (HTML mode, or the author never clicked into the editor): at the end.
    const inserted =
      latest.current.mode === "visual" &&
      !!editor &&
      cursorPlaced.current &&
      insertAfterCursorBlock(editor, galleryHtml(items, { withHeading: !hasGalleryHeading(current) }));
    if (inserted) {
      setDraft(editor.getData());
      setTouched(true);
    } else {
      const next = appendGallery(current, items);
      setDraft(next);
      setTouched(true);
      if (latest.current.mode === "visual" && editor) editor.setData(next);
    }
    setGallery({ busy: false, done: 0, total: 0 });
    Swal.fire({ title: `${items.length} image(s) added to the gallery`, icon: "success", timer: 1800, showConfirmButton: false });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setBannerWarning("");
    if (!file) {
      setErrors({ ...errors, image: "" });
      setSelectedFile(null);
      setFilePreview(null);
      return;
    }
    const problem = validateNewsImage(file);
    if (problem) {
      setErrors({ ...errors, image: problem });
      return;
    }
    setErrors({ ...errors, image: "" });
    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setFilePreview(objectUrl);
    const probe = new Image();
    probe.onload = () => {
      const ratio = probe.naturalWidth / probe.naturalHeight;
      if (Math.abs(ratio - BANNER_RATIO) / BANNER_RATIO > 0.02) {
        setBannerWarning(
          `This image is ${probe.naturalWidth} x ${probe.naturalHeight}. Banners show in a 16:9 frame, so it will be letterboxed. 1280 x 720 is recommended.`
        );
      }
    };
    probe.src = objectUrl;
  };

  const handleDeleteFile = () => {
    Swal.fire({
      title: "Replace image?",
      text: "Please select a new image to replace the current one.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Choose new image",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedFile(null);
        setFilePreview(null);
        setExistingBannerURL(null);
        setBannerWarning("");
        setErrors({ ...errors, image: "Please select a new image" });
      }
    });
  };

  const validateForm = (description) => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!String(description || "").trim()) newErrors.description = "Description is required";
    if (!categoryId.length) newErrors.category = "Category is required";
    if (!status) newErrors.status = "Status is required";
    if (!(selectedFile || existingBannerURL)) newErrors.image = "Image is required";
    if (normalizeEventUrl(eventUrl) === null) {
      newErrors.event_url = "Enter a valid http(s) URL, e.g. https://luma.com/event/…";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!guard.checked) return; // the editor is still loading the stored post
    if (pendingUploads || gallery.busy) {
      Swal.fire({ title: "Images are still uploading", text: "Wait until every image has finished uploading, then save again.", icon: "info" });
      return;
    }
    const description = currentDescription();
    if (!validateForm(description)) return;

    const warnings = lintDescription({ html: description, categories: categoryNames, eventDate, eventUrl });
    if (warnings.length) {
      const answer = await Swal.fire({
        title: "Before you save",
        html: `<ul style="text-align:left">${warnings.map((w) => `<li>${escapeHtml(w.message)}</li>`).join("")}</ul>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Save anyway",
        cancelButtonText: "Go back and fix",
      });
      if (!answer.isConfirmed) return;
    }

    const sent = {
      title,
      description,
      event_url: normalizeEventUrl(eventUrl) || "",
      // Midday UTC, not midnight: the column is a plain DATETIME and midnight
      // lets timezone skew land on the adjacent day. "" clears it deliberately.
      event_date: eventDate ? `${eventDate}T12:00:00.000Z` : "",
    };
    const formData = new FormData();
    formData.append("title", sent.title);
    // Sent as written: the API sanitizes server-side (that is the boundary), and
    // re-serializing here would reshape posts that were never edited.
    formData.append("description", sent.description);
    formData.append("category", JSON.stringify(categoryId));
    formData.append("bannerType", "image");
    formData.append("status", JSON.stringify(status.value));
    formData.append("event_url", sent.event_url);
    formData.append("event_date", sent.event_date);
    if (selectedFile && selectedFile instanceof File) formData.append("newBanner", selectedFile);

    try {
      setLoading(true);
      const response = await axios({
        method: isAdd ? "post" : "patch",
        url: `${apiBase}admin/news${isAdd ? "" : "/" + newsId}`,
        // No Content-Type: axios adds the multipart boundary for FormData.
        headers: { "x-access-token": token },
        data: formData,
      });
      // Read the row back: the API answers 200 even when its sanitizer strips
      // images, so this is the only way to tell the author what was kept.
      const savedId = isAdd ? response.data?.data?.id : newsId;
      let loss = null;
      if (savedId) {
        try {
          const stored = await axios.get(`${apiBase}admin/news/${savedId}`, { headers: { "x-access-token": token } });
          loss = describeReadBackLoss(sent, stored.data.data);
        } catch (_) {
          loss = null;
        }
      }
      setLoading(false);
      if (loss && loss.any) {
        const lines = [];
        if (loss.removedImages.length) lines.push(`${loss.removedImages.length} image(s) were removed by the server (not uploaded through this form).`);
        if (loss.changedFields.length) lines.push(`These fields were stored differently than sent: ${loss.changedFields.join(", ")}.`);
        if (loss.contentChanged) lines.push("Parts of the description (text, layout or embedded elements) were changed or removed by the server. Open the post again to check it.");
        await Swal.fire({
          title: "Saved, but not everything was kept",
          html: lines.map((l) => `<p>${escapeHtml(l)}</p>`).join(""),
          icon: "warning",
          confirmButtonText: "OK",
        });
      } else {
        await Swal.fire({ title: response.data.message, icon: "success", confirmButtonText: "OK" });
      }
      navigate("/news");
    } catch (error) {
      setLoading(false);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save news";
      Swal.fire({ title: errorMessage, icon: "error", confirmButtonText: "OK" });
    }
  };

  const editorLoading = !isAdd && loadedHtml === null;

  return (
    <PageShell>
      <PageHead title={isAdd ? "Add news" : "Edit news"} />
      <div className="new">
      <div className="newContainer">
        <Loader isLoading={loading} />
        <div className="bottom">
          <div>
            <form onSubmit={handleSubmit} className="faq-form">
              <div className="custome-form-group">
                <label htmlFor="news-title">Title*:</label>
                <input
                  id="news-title"
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setErrors({ ...errors, title: "" });
                  }}
                  className="custome-input"
                  style={{ width: "524px" }}
                />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>
              <div className="custome-form-group"></div>
              <div className="custome-form-group">
                <label>Category*:</label>
                <Select
                  isMulti
                  inputId="news-category"
                  options={categories}
                  value={categories.filter((cat) => categoryId.includes(cat.value))}
                  onChange={(selected) => {
                    setCategoryId(selected ? selected.map((sel) => sel.value) : []);
                    setErrors({ ...errors, category: "" });
                  }}
                  className="sort-by dropdown country-drp"
                />
                <small className="text-muted d-block" id="news-category-page">{categoryPageHint(categoryNames)}</small>
                {errors.category && <div className="invalid-feedback">{errors.category}</div>}
              </div>
              <div className="custome-form-group"></div>
              <div className="custome-form-group">
                <label>Status*:</label>
                <Select
                  inputId="news-status"
                  options={statusOptions}
                  value={status}
                  onChange={(selected) => {
                    setStatus(selected);
                    setErrors({ ...errors, status: "" });
                  }}
                  className="sort-by dropdown country-drp"
                />
                {errors.status && <div className="invalid-feedback">{errors.status}</div>}
              </div>
              <div className="custome-form-group"></div>
              <div className="custome-form-group">
                <label htmlFor="news-banner">Banner Image*:</label>
                <input
                  id="news-banner"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => handleFileChange(e)}
                />
                <small className="text-muted d-block">16:9 image, 1280 x 720 recommended (JPG, PNG, WEBP or GIF, up to 10 MB).</small>
                {bannerWarning && <div className="news-editor__warning" role="status">{bannerWarning}</div>}
                {errors.image && <div className="invalid-feedback">{errors.image}</div>}
              </div>
              {filePreview && (
                <div className="image-preview-container">
                  <div className="image-preview">
                    <img src={filePreview} alt="File Preview" style={{ maxWidth: "200px" }} />
                  </div>
                  <button type="button" className="delete-image" onClick={handleDeleteFile}>
                    Replace Image
                  </button>
                </div>
              )}
              <div className="custome-form-group news-editor" style={{ width: "95%" }}>
                <div className="news-editor__bar">
                  <label>Description*:</label>
                  <div className="news-editor__modes" role="group" aria-label="Editor mode">
                    <button type="button" className={mode === "visual" ? "is-active" : ""} aria-pressed={mode === "visual"} onClick={switchToVisual} disabled={!guard.checked || gallery.busy}>
                      Visual
                    </button>
                    <button type="button" className={mode === "html" ? "is-active" : ""} aria-pressed={mode === "html"} onClick={switchToHtml} disabled={!guard.checked || gallery.busy || pendingUploads}>
                      HTML
                    </button>
                  </div>
                  <button type="button" className="news-editor__gallery" onClick={() => galleryInputRef.current && galleryInputRef.current.click()} disabled={!guard.checked || gallery.busy || pendingUploads}>
                    {gallery.busy ? `Uploading ${gallery.done}/${gallery.total}…` : "Add gallery images"}
                  </button>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: "none" }}
                    data-testid="news-gallery-input"
                    onChange={(e) => {
                      handleGalleryFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </div>
                {guard.mismatch && mode === "html" && (
                  <div className="news-editor__notice" role="status">
                    This post was written as HTML (for example a gallery or layout the visual editor does not support), so it opened in HTML mode to keep it exactly as published. Edit the HTML here, or switch to Visual if you accept that it may change.
                  </div>
                )}
                {conversion && conversion.changed && (
                  <div className="news-editor__warning" role="alert">
                    The visual editor changed parts of this post.{" "}
                    <button type="button" className="news-editor__link" onClick={restoreHtml}>Restore the HTML</button>
                  </div>
                )}
                {editorLoading && <div className="news-editor__notice">Loading the post…</div>}
                {!editorLoading && mode === "visual" && (
                  <CKEditor
                    key={editorKey}
                    editor={ClassicEditor}
                    data={conversion ? conversion.snapshot : draft}
                    config={{ toolbar: EDITOR_TOOLBAR, extraPlugins: [uploadPlugin] }}
                    onReady={handleEditorReady}
                    onChange={handleEditorChange}
                  />
                )}
                {!editorLoading && mode === "html" && (
                  <textarea
                    className="news-editor__html"
                    aria-label="Description HTML"
                    value={draft}
                    readOnly={gallery.busy}
                    spellCheck={false}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      setTouched(true);
                      setErrors({ ...errors, description: e.target.value.trim() ? "" : "Description is required" });
                    }}
                  />
                )}
                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                <details className="news-editor__help">
                  <summary>How events show on the website</summary>
                  <ul>
                    <li><strong>Images:</strong> the website shows every image where you place it. Two or more images in a row (nothing but empty lines between them) become a slideshow with arrows; a single image stays in the text.</li>
                    <li><strong>Gallery:</strong> click where the gallery should go, then use "Add gallery images" (Canva: Share, Download, PNG or JPG, then pick all the pages). The images are added after the paragraph the cursor is in, with an "Event Gallery" heading if the post has none yet. If you have not clicked into the text, they are added at the end.</li>
                    <li><strong>Video:</strong> paste the YouTube link on its own line; the website shows the player in that spot. A link inside a sentence stays a link, with the player after that paragraph. Embed codes are removed.</li>
                    <li><strong>LinkedIn and other links:</strong> select text and use the link button.</li>
                    <li><strong>Luma:</strong> put the Luma link in Event URL. Before the event the page shows "Register for Event"; after the event date it shows "View event on Luma".</li>
                    <li><strong>Banner:</strong> 16:9, 1280 x 720 recommended. It is the card image on the Events page.</li>
                    <li>Save as <strong>Draft</strong> first, check it, then set Status to Published.</li>
                  </ul>
                </details>
              </div>
              <div className="custome-form-group" style={{ width: "95%" }}>
                <label htmlFor="news-event-url">Event URL (optional):</label>
                <input
                  id="news-event-url"
                  type="url"
                  className="form-control"
                  placeholder="https://luma.com/event/..."
                  value={eventUrl}
                  onChange={(e) => {
                    setEventUrl(e.target.value);
                    setErrors({ ...errors, event_url: "" });
                  }}
                />
                <small className="text-muted">
                  Paste the Luma event URL. Before the event the page shows a "Register for Event" button, after it "View
                  event on Luma". Only http(s) links are accepted; leave blank for an ordinary article.
                </small>
                {errors.event_url && <div className="invalid-feedback">{errors.event_url}</div>}
              </div>
              <div className="custome-form-group" style={{ width: "95%" }}>
                <label htmlFor="news-event-date">Event date (optional):</label>
                <input
                  id="news-event-date"
                  type="date"
                  className="form-control"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
                <small className="text-muted">
                  The date the event takes place. The Events page shows it on the card, sorts by it and uses it to tell
                  upcoming from past events. Leave blank for an ordinary article.
                </small>
              </div>
              <button type="submit" className="approveButton" disabled={!guard.checked || gallery.busy || pendingUploads}>
                {isAdd ? "Save" : "Update"}
              </button>
            </form>
          </div>
        </div>
      </div>
      </div>
    </PageShell>
  );
};

export default NewsAddEdit;
